import React, { useState } from "react";
import axios from "axios";
import { X, KeyRound, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ResetPasswordModal({ resetToken, onClose }) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const inputCls = "w-full bg-white border border-[#D2E0E6] text-[#0B2830] mono text-sm px-3 py-2.5 rounded-md focus:border-[#0284C7] focus:ring-1 focus:ring-[#0284C7] outline-none";

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next.length < 8) { setError("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: next });
      setDone(true);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-[#0B2830]/70 flex items-center justify-center z-50 p-4" data-testid="reset-pw-modal">
      <div className="bg-white border border-[#D2E0E6] rounded-lg shadow-xl w-full max-w-sm fade-up">
        <div className="flex items-center justify-between border-b border-[#D2E0E6] px-5 py-4">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#0284C7]" />
            <h3 className="font-semibold text-[#0B2830]">Set a new password</h3>
          </div>
          <button data-testid="reset-pw-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center space-y-3" data-testid="reset-pw-done">
            <CheckCircle2 className="w-10 h-10 text-[#0D9488] mx-auto" />
            <h4 className="font-semibold text-[#0B2830]">Password updated</h4>
            <p className="text-sm text-[#47616B]">You can now sign in with your new password.</p>
            <button data-testid="reset-pw-done-close" onClick={onClose}
              className="mt-2 w-full bg-[#0284C7] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#0369A1]">
              Go to login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">New password</label>
              <input data-testid="reset-pw-new" type="password" value={next} autoFocus
                onChange={(e) => setNext(e.target.value)} className={inputCls} placeholder="At least 8 characters" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-[#7C949C] mb-1.5">Confirm password</label>
              <input data-testid="reset-pw-confirm" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} className={inputCls} placeholder="Re-enter new password" />
            </div>
            {error && (
              <div data-testid="reset-pw-error" className="text-sm text-[#E11D48] border border-[#E11D48]/30 bg-[#E11D48]/5 rounded-md px-3 py-2">{error}</div>
            )}
            <button data-testid="reset-pw-submit" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-[#0284C7] text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-[#0369A1] transition-colors disabled:opacity-50">
              <KeyRound className="w-4 h-4" /> {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
